import { useState } from "react";
import {
  useGetAllNotificationsQuery,
  useRemoveNotificationFilesMutation,
} from "../api/notificationApi";
import { showToast } from "../../../core/utils/alert";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";

export const useNotifications = (initialPageSize = 10) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [searchTerm, setSearchTerm] = useState("");
  const [removeFiles] = useRemoveNotificationFilesMutation();

  // Get user role to determine if created=true parameter should be added
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "superadmin";

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetAllNotificationsQuery(
      {
        page,
        size: pageSize,
        search: searchTerm,
        // Add created=true parameter for non-superadmin users
        ...(isSuperAdmin ? {} : { created: true }),
      },
      { refetchOnMountOrArgChange: true }
    );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1); // Reset to first page when searching
  };

  // New function to handle file removal
  const handleRemoveFiles = async (
    notificationId: string,
    filenames: string[],
    onSuccess?: () => void
  ) => {
    try {
      const result = await removeFiles({
        id: notificationId,
        filenames,
      }).unwrap();

      if (result.success) {
        showToast({
          title: "Success",
          text: "File(s) removed successfully",
          icon: "success",
        });

        if (onSuccess) {
          onSuccess();
        }

        return true;
      } else {
        showToast({
          title: "Error",
          text: result.error || "Failed to remove file(s)",
          icon: "error",
        });
        return false;
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Failed to remove file(s)",
        icon: "error",
      });
      console.error("Error removing files:", error);
      return false;
    }
  };

  return {
    notifications: data?.data?.items || [],
    paginationData: {
      currentPage: data?.data?.currentPage || 1,
      pageSize: data?.data?.pageSize || pageSize,
      totalItems: data?.data?.totalItems || 0,
      totalPages: data?.data?.totalPages || 0,
    },
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    handlePageChange,
    handlePageSizeChange,
    handleSearch,
    handleRemoveFiles,
    searchTerm,
  };
};
